/**
 * Timeline widget — an in-scene, moveable GUI that acts as a global timeline
 * driver. Finds all root SceneObjects with a Timeline behavior, reports the
 * union duration, and fans play/pause/seek out to each.
 *
 * Interaction: uses scene.onPrePointerObservable + ray picking against the
 * widget plane, with camera detach/reattach during interaction. Same pattern
 * as Video's scrub bar — this works reliably in both edit mode and play mode,
 * blocks camera orbit during drag, and plays well with XR controller rays.
 * GUI controls are visual-only; no Button.onPointer observables.
 */

import type { SceneObject, TraceScene } from 'trace-viewer';
import { topLevelObjects } from 'trace-viewer/editor';
import { Billboard, Moveable, Pages, Timeline, CONTROLS_MESH_SUFFIX } from 'trace-viewer/behaviors';

import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { Scene } from '@babylonjs/core/scene';
import type { Observer } from '@babylonjs/core/Misc/observable';
import { PointerEventTypes } from '@babylonjs/core/Events/pointerEvents';
import type { PointerInfoPre } from '@babylonjs/core/Events/pointerEvents';

import { AdvancedDynamicTexture } from '@babylonjs/gui/2D/advancedDynamicTexture';
import { Rectangle } from '@babylonjs/gui/2D/controls/rectangle';
import { TextBlock } from '@babylonjs/gui/2D/controls/textBlock';
import { Control } from '@babylonjs/gui/2D/controls/control';

const PLANE_WIDTH = 1.2;
const PLANE_HEIGHT = 0.25;
const TEXTURE_WIDTH = 960;
const TEXTURE_HEIGHT = 200;

// Hit regions expressed as normalised UV on the plane (origin bottom-left).
// Plane is 960px texture wide. Hit regions are slightly padded beyond the
// visual glyph rectangles for generous pointer targets.
//   play/pause:   glyph  30..150  → hit UV 0.02..0.17
//   track:        full   170..690 → hit UV 0.177..0.719  (520px)
//   show-all btn: glyph 710..830  → hit UV 0.73..0.864  (only when Pages present)
//   time text:    840..930 (right-aligned, non-interactive — hit region of
//                  show-all must stop before this so clicks on the readout
//                  don't fire show-all)
const BUTTON_UV = { xMin: 0.02, xMax: 0.17 } as const;
const TRACK_UV = { xMin: 0.177, xMax: 0.719 } as const;
const SHOW_ALL_UV = { xMin: 0.73, xMax: 0.864 } as const;

/**
 * Create a timeline widget SceneObject in the scene and return it.
 * The caller is responsible for undo-recording and selection.
 */
export async function createTimelineWidget(
    traceScene: TraceScene,
    parent?: SceneObject | null,
): Promise<SceneObject> {
    const obj = await traceScene.createSceneObject('TimelineWidget', parent ?? undefined);

    // Default placement: in front of the camera at a readable distance, but
    // only when the widget is at scene root — otherwise `getFrontPosition`
    // returns world-space coords that would be wrong in the parent's local
    // frame. Callers (e.g. mapanything-glb) override this explicitly.
    if (!parent) {
        const camera = traceScene.getCamera();
        if (camera) {
            const front = camera.getFrontPosition(1.5);
            obj.transform.position.copyFrom(front);
        } else {
            obj.transform.position.set(0, 1.2, 1.5);
        }
    }

    await obj.addBehavior(Moveable);

    // Build the GUI meshes BEFORE attaching Billboard. Billboard rotates the
    // animationRoot, not transform; getOrCreateAnimationRoot() reparents
    // existing children once, so meshes added after Billboard's load() would
    // sit on transform and never rotate.
    attachTimelineWidgetUI(obj);

    await obj.addBehavior(Billboard);

    return obj;
}

/**
 * Attach the Babylon GUI plane and driving logic to a SceneObject.
 * Disposes cleanly when the object's transform is disposed.
 */
function attachTimelineWidgetUI(sceneObject: SceneObject): void {
    const scene: Scene = sceneObject.transform.getScene();

    // Plane name uses the __controls suffix so XRManager re-clears
    // isNearGrabbable on us when SixDofDragBehavior attaches (otherwise the
    // hand near-grab path outranks pointer-ray picks and our buttons/scrub
    // become unreachable in XR — same problem Video's timeline bar solves).
    const plane: Mesh = MeshBuilder.CreatePlane(
        `timeline-widget${CONTROLS_MESH_SUFFIX}`,
        { width: PLANE_WIDTH, height: PLANE_HEIGHT },
        scene,
    );
    plane.parent = sceneObject.transform;
    plane.isPickable = true;
    plane.isNearGrabbable = false;

    // Invisible thin box that gives the widget a 3D body — DragToMove (play
    // mode), ClickToSelect (background presses), and XR SixDof grab all need
    // a mesh with depth to grab. Same trick as Video's auto picker mesh.
    // Pushed back along local -Z so the plane is unambiguously the front-most
    // pickable surface from the camera/controller's approach angle. Without
    // this offset, XR controller rays hit the box first and the plane's UV
    // (used for button/scrub hit-regions) is never read.
    const pickerBox: Mesh = MeshBuilder.CreateBox(
        'timeline-widget__picker',
        { width: PLANE_WIDTH, height: PLANE_HEIGHT, depth: 0.02 },
        scene,
    );
    pickerBox.parent = sceneObject.transform;
    // Plane front is +Z. Push box behind the plane so picks from the front
    // prefer the plane (UV reads) and only fall through to the box for misses.
    pickerBox.position.z = -0.015;
    pickerBox.visibility = 0;
    pickerBox.isPickable = true;
    // Intentionally NOT setting isNearGrabbable=false here, unlike the plane.
    // In play-mode XR, Moveable attaches SixDofDragBehavior to the widget
    // transform, which sets isNearGrabbable=true on all descendants. The
    // plane opts out via CONTROLS_MESH_SUFFIX (XRManager clears the flag) so
    // the hand-grab path doesn't steal its UV picks. The picker box
    // deliberately stays grabbable — it's the widget's hand-grab handle for
    // repositioning in XR, analogous to grabbing the frame rather than the
    // screen. Pointer-ray interaction with the plane still works because
    // scrub picks filter to `m === plane` (see pickPlane()).

    const planeMat = new StandardMaterial('timeline-widget-mat', scene);
    planeMat.backFaceCulling = false;
    planeMat.disableLighting = true;
    planeMat.emissiveColor = new Color3(1, 1, 1);
    plane.material = planeMat;

    const texture = AdvancedDynamicTexture.CreateForMesh(plane, TEXTURE_WIDTH, TEXTURE_HEIGHT, false);

    // ── Visual layout (no GUI pointer observers — interaction is via scene picks) ──
    const root = new Rectangle('tw-root');
    root.thickness = 0;
    root.background = 'rgba(20, 20, 24, 0.85)';
    root.cornerRadius = 18;
    root.width = 1;
    root.height = 1;
    texture.addControl(root);

    // Layout uses left-aligned absolute positions so visual placement
    // matches the UV hit-region constants above.

    // Play/pause pad: pixels 30..150
    const buttonPad = new Rectangle('tw-button-pad');
    buttonPad.width = '120px';
    buttonPad.height = '120px';
    buttonPad.thickness = 0;
    buttonPad.background = '#2a6df5';
    buttonPad.cornerRadius = 16;
    buttonPad.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    buttonPad.left = '30px';
    root.addControl(buttonPad);

    const buttonIcon = new TextBlock('tw-button-icon', '▶');
    buttonIcon.color = 'white';
    buttonIcon.fontSize = 56;
    buttonPad.addControl(buttonIcon);

    // Track + fill + thumb: pixels 170..690 (520px wide). Visual only — picks
    // come from plane UV.
    const TRACK_PX = 520;
    const THUMB_PX = 36;
    const track = new Rectangle('tw-track');
    track.width = `${TRACK_PX}px`;
    track.height = '36px';
    track.thickness = 0;
    track.background = '#444';
    track.cornerRadius = 18;
    track.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    track.left = '170px';
    root.addControl(track);

    const trackFill = new Rectangle('tw-track-fill');
    trackFill.thickness = 0;
    trackFill.background = '#2a6df5';
    trackFill.cornerRadius = 18;
    trackFill.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    trackFill.height = 1;
    trackFill.width = 0;
    track.addControl(trackFill);

    const thumb = new Rectangle('tw-thumb');
    thumb.width = `${THUMB_PX}px`;
    thumb.height = '44px';
    thumb.thickness = 0;
    thumb.background = 'white';
    thumb.cornerRadius = 8;
    thumb.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    thumb.left = 0;
    track.addControl(thumb);

    // Show-all pad: pixels 710..830. Hidden when no Pages behavior exists in
    // the scene.
    const showAllPad = new Rectangle('tw-showall-pad');
    showAllPad.width = '120px';
    showAllPad.height = '120px';
    showAllPad.thickness = 0;
    showAllPad.background = '#444';
    showAllPad.cornerRadius = 16;
    showAllPad.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    showAllPad.left = '710px';
    showAllPad.isVisible = false;
    root.addControl(showAllPad);

    // Grid glyph as a "show all" affordance.
    const showAllIcon = new TextBlock('tw-showall-icon', '▦');
    showAllIcon.color = 'white';
    showAllIcon.fontSize = 64;
    showAllPad.addControl(showAllIcon);

    // Time text: pixels 850..930, right-aligned. Non-interactive.
    const timeText = new TextBlock('tw-time', '0.0 / 0.0s');
    timeText.color = 'white';
    timeText.fontSize = 24;
    timeText.width = '110px';
    timeText.height = '40px';
    timeText.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
    timeText.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    timeText.left = '820px';
    root.addControl(timeText);

    // ── Timeline / Pages discovery ──
    const getRootTimelines = (): Timeline[] => {
        const timelines: Timeline[] = [];
        const roots = topLevelObjects(sceneObject.traceScene);
        for (const child of roots) {
            if (child === sceneObject) continue;
            const t = child.getBehavior(Timeline);
            if (t) timelines.push(t);
        }
        return timelines;
    };

    const getRootPages = (): Pages[] => {
        const out: Pages[] = [];
        const roots = topLevelObjects(sceneObject.traceScene);
        for (const child of roots) {
            if (child === sceneObject) continue;
            const p = child.getBehavior(Pages);
            if (p) out.push(p);
        }
        return out;
    };

    /**
     * Toggle the show-all overview across every root Pages behavior. If any is
     * currently showing all, restore each to its remembered active page.
     * Otherwise show all and pause every root Timeline (overview mode is for
     * inspection — playing while spread out is rarely what you want).
     */
    const toggleShowAll = (): void => {
        const pages = getRootPages();
        if (pages.length === 0) return;
        const anyShowingAll = pages.some(p => p.isShowingAll());
        if (anyShowingAll) {
            for (const p of pages) {
                if (p.isShowingAll()) p.showPage(p.getActivePageIndex());
            }
            return;
        }
        for (const p of pages) p.showAllPages();
        for (const t of getRootTimelines()) t.pause();
    };

    // ── Pointer interaction (Video-pattern) ──
    let scrubbing = false;
    let suppressNextPointerUp = false;

    const pickPlane = (pi: PointerInfoPre) => {
        // In XR, Babylon's input manager populates originalPickingInfo with
        // the controller's ray pick. If that already hit our plane we can
        // use its UV directly. Otherwise re-pick (filtered to plane only) so
        // hits on the picker box behind the plane still land button/scrub.
        const orig = pi.originalPickingInfo;
        if (orig?.hit && orig.pickedMesh === plane) {
            return orig;
        }
        if (pi.ray) {
            return scene.pickWithRay(pi.ray, (m) => m === plane);
        }
        return scene.pick(
            pi.localPosition.x, pi.localPosition.y,
            (m) => m === plane,
        );
    };

    const scrubFromUV = (u: number): void => {
        const timelines = getRootTimelines();
        const fraction = Math.max(0, Math.min(1,
            (u - TRACK_UV.xMin) / (TRACK_UV.xMax - TRACK_UV.xMin),
        ));
        for (const t of timelines) {
            t.seek(fraction * t.getDuration());
        }
    };

    const togglePlayback = (): void => {
        const timelines = getRootTimelines();
        if (timelines.length === 0) return;
        const anyPlaying = timelines.some(t => t.isPlaying());
        for (const t of timelines) {
            if (anyPlaying) t.pause();
            else t.play();
        }
    };

    const detachCamera = (): void => {
        scene.activeCamera?.detachControl();
    };
    const reattachCamera = (): void => {
        const canvas = scene.getEngine().getRenderingCanvas();
        if (scene.activeCamera && canvas) {
            scene.activeCamera.attachControl(canvas, true);
        }
    };

    const pointerObserver: Observer<PointerInfoPre> | null = scene.onPrePointerObservable.add((pi) => {
        switch (pi.type) {
            case PointerEventTypes.POINTERDOWN: {
                const pick = pickPlane(pi);
                if (!pick?.hit) return;
                const uv = pick.getTextureCoordinates();
                if (!uv) return;

                if (uv.x >= BUTTON_UV.xMin && uv.x <= BUTTON_UV.xMax) {
                    // Button press — consume down AND the matching up so selection / Moveable don't react.
                    pi.skipOnPointerObservable = true;
                    suppressNextPointerUp = true;
                    togglePlayback();
                    return;
                }

                if (uv.x >= TRACK_UV.xMin && uv.x <= TRACK_UV.xMax) {
                    scrubbing = true;
                    pi.skipOnPointerObservable = true;
                    detachCamera();
                    scrubFromUV(uv.x);
                    return;
                }

                if (showAllPad.isVisible
                    && uv.x >= SHOW_ALL_UV.xMin && uv.x <= SHOW_ALL_UV.xMax) {
                    pi.skipOnPointerObservable = true;
                    suppressNextPointerUp = true;
                    toggleShowAll();
                    return;
                }
                // Dead zone (e.g. time-text area): let the event through so the
                // user can still click-select the widget and drag it.
                break;
            }
            case PointerEventTypes.POINTERMOVE: {
                if (!scrubbing) return;
                pi.skipOnPointerObservable = true;
                const pick = pickPlane(pi);
                if (pick?.hit) {
                    const uv = pick.getTextureCoordinates();
                    if (uv) scrubFromUV(uv.x);
                }
                break;
            }
            case PointerEventTypes.POINTERUP: {
                if (scrubbing) {
                    scrubbing = false;
                    pi.skipOnPointerObservable = true;
                    reattachCamera();
                    return;
                }
                if (suppressNextPointerUp) {
                    suppressNextPointerUp = false;
                    pi.skipOnPointerObservable = true;
                }
                break;
            }
        }
    });

    // ── Per-frame visual sync ──
    const renderObserver: Observer<Scene> | null = scene.onBeforeRenderObservable.add(() => {
        showAllPad.isVisible = getRootPages().length > 0;

        const timelines = getRootTimelines();
        if (timelines.length === 0) {
            buttonIcon.text = '▶';
            timeText.text = 'no timelines';
            trackFill.width = 0;
            thumb.left = 0;
            return;
        }

        const maxDuration = timelines.reduce((m, t) => Math.max(m, t.getDuration()), 0);
        const maxCurrent = timelines.reduce((m, t) => Math.max(m, t.getTime()), 0);
        const anyPlaying = timelines.some(t => t.isPlaying());

        buttonIcon.text = anyPlaying ? '❚❚' : '▶';
        timeText.text = `${maxCurrent.toFixed(1)} / ${maxDuration.toFixed(1)}s`;

        const fraction = maxDuration > 0 ? maxCurrent / maxDuration : 0;
        trackFill.width = `${fraction * TRACK_PX}px`;
        thumb.left = `${fraction * (TRACK_PX - THUMB_PX)}px`;
    });

    // ── Cleanup ──
    plane.onDisposeObservable.add(() => {
        if (scrubbing) reattachCamera();
        if (renderObserver) scene.onBeforeRenderObservable.remove(renderObserver);
        if (pointerObserver) scene.onPrePointerObservable.remove(pointerObserver);
        texture.dispose();
        planeMat.dispose();
    });
}
